import React, { useCallback, useMemo, useState } from 'react'
import { WhenDropdownContent } from '../WhenDropdown'
import { DeadlineDropdownContent } from '../DeadlineDropdown'
import { DurationDropdownContent } from '../DurationDropdown'
import { AttentionDropdownContent } from '../AttentionDropdown'
import { TagDropdownContent } from '../TagDropdown'
import { RewardDropdownContent } from '../RewardDropdown'
import { RewardPickerContent } from '../RewardPicker'
import { RecurrenceDropdownContent } from '../RecurrenceDropdown'
import type { RemovableMarkers } from '../../utils/removableMarkers'
import type { TaskMenuHandlers, TaskMenuTarget } from './TaskMenu.types'
import type { TaskEntryData } from '../TaskEntry'

/**
 * Der Inhalt der **Belohnungs**-Zeile — zwei Bilder in einem Kasten, wie im
 * `TaskRewardTrigger` der aufgeklappten Aufgabe: erst die Wahl zwischen
 * **Belohnung** und **Ziel**, dann der Katalog.
 *
 * Getauscht statt geschachtelt: Ein drittes Unter-Menü für den Katalog wäre ein
 * Raster in einem Menü in einem Menü. Hängt schon eine Belohnung dran, beginnt
 * er gleich im Katalog — dann will man tauschen, nicht wählen. Ebenso, wenn es in der
 * Mehrfachauswahl etwas zu entfernen gibt (`removable`): Der Papierkorb steht im Katalog.
 *
 * „Mein Ziel dahinter" hat noch keinen Weg hinter sich; es schließt, genau wie
 * in der Leiste, die es ablöst.
 */
const RewardContent: React.FC<{
  value: string | null
  removable: boolean
  onValueChange: (rewardId: string | null) => void
  onClose: () => void
}> = ({ value, removable, onValueChange, onClose }) => {
  const [view, setView] = useState<'menu' | 'picker'>(value || removable ? 'picker' : 'menu')

  if (view === 'picker') {
    return (
      <RewardPickerContent
        value={value}
        removable={removable}
        onValueChange={(picked) => { onValueChange(picked); onClose() }}
        onClose={onClose}
        placement="actionbar"
      />
    )
  }

  return (
    <RewardDropdownContent
      onSelect={(action) => { if (action === 'reward') setView('picker'); else onClose() }}
      /*
        **Hier schließt nur `onSelect`.**

        `RewardDropdownContent` ruft `onClose` unmittelbar nach `onSelect` — im
        SELBEN Ereignis, also lange bevor das `setView` oben ein Bild später
        ankommt. Ein Blick auf `view` sagte an dieser Stelle deshalb immer noch
        „menu" und schlösse den Kasten weg, in dem der Katalog gerade
        aufmachen will (gemeldet 2026-09-11: `G`, dann „Belohnung hinzufügen …"
        — und nichts geschah).

        Was geschlossen wird, entscheidet also `onSelect` allein.
      */
      onClose={() => {}}
      placement="actionbar"
      // Die Ziffern gehören im Aufgaben-Menü den Schnellwahlen der
      // Datums-Zeilen; zwei Bedeutungen zwei Zeilen auseinander lernt niemand.
      shortcuts={false}
    />
  )
}

/**
 * **Was hinter einer Zeile des Aufgaben-Menüs steckt** — je Ziel genau ein
 * Inhalt.
 *
 * Er steht als eigene Komponente da, weil ihn ZWEI Wege brauchen: das
 * aufgeklappte Unter-Menü im Rechtsklick-Menü und das **Tastenkürzel**, das
 * dieselbe Sache ohne Menü öffnet (`TaskEntry`, Anker an der Zeile). Stünden
 * die Inhalte an beiden Stellen getippt, liefe eine der beiden früher oder
 * später hinterher.
 *
 * Jeder Inhalt schließt, sobald er einen Wert geliefert hat — das entscheidet
 * nicht er, sondern `onClose` des Aufrufers.
 */
export const TaskMenuTargetContent: React.FC<{
  target: TaskMenuTarget
  task: TaskEntryData
  handlers: TaskMenuHandlers
  /**
   * **„Ruhen lassen"** im Wann-Kasten — derselbe Griff wie die eigene Zeile des
   * Menüs. Dass das Menü die Handlung damit zweimal führt, ist hingenommen
   * (CONTEXT.md): Wer im Kalender steht, soll nicht erst wieder heraus.
   */
  onRest?: () => void
  /**
   * **Was sich entfernen lässt, wenn nicht die gezeigte Aufgabe es sagt** — die Pille bei Mehrfachauswahl:
   * sobald IRGENDEINE der Aufgaben die Marke trägt (#313, `removableMarkers`). Fehlt es, zählt der Wert von `task`.
   */
  removable?: RemovableMarkers
  onClose: () => void
}> = ({ target, task, handlers, onRest, removable, onClose }) => {
  const startValue = useMemo(
    () => (task.startDate
      ? { date: task.startDate, time: task.startTime ?? null, period: task.startPeriod ?? null }
      : null),
    [task.startDate, task.startTime, task.startPeriod]
  )

  const deadlineValue = useMemo(
    () => (task.dueDate
      ? { date: task.dueDate, time: task.dueTime ?? null, period: task.duePeriod ?? null }
      : null),
    [task.dueDate, task.dueTime, task.duePeriod]
  )

  const durationValue = useMemo(
    () => (task.estimatedDuration
      ? { minutes: task.estimatedDuration, mode: task.durationMode ?? 'duration' as const }
      : null),
    [task.estimatedDuration, task.durationMode]
  )

  const canRemove = (marker: keyof RemovableMarkers, own: boolean) => (removable ? !!removable[marker] : own)

  /** Wert setzen und schließen — so endet jeder der fünf Inhalte. */
  const commit = useCallback(<T,>(apply: (value: T) => void) => (value: T) => {
    apply(value)
    onClose()
  }, [onClose])

  switch (target) {
    case 'start':
      return (
        <WhenDropdownContent
          value={startValue}
          onValueChange={commit(handlers.onStartChange)}
          onClose={onClose}
          onRest={onRest}
          // **Entfernen, sobald ein Wert dasteht** (#216). Der Fuß schließt selbst — ein zweites
          // `onClose` schickte im Menü ein zweites Escape hinterher.
          onRemove={canRemove('start', !!startValue) ? () => handlers.onStartChange(null) : undefined}
          placement="actionbar"
        />
      )
    case 'duration':
      return (
        <DurationDropdownContent
          value={durationValue}
          onValueChange={commit(handlers.onDurationChange)}
          onClose={onClose}
          // Hier schließt der Knopf nicht selbst — also `commit` (#216).
          onRemove={canRemove('duration', !!durationValue) ? () => commit(handlers.onDurationChange)(null) : undefined}
          placement="actionbar"
        />
      )
    case 'deadline':
      return (
        <DeadlineDropdownContent
          value={deadlineValue}
          onValueChange={commit(handlers.onDeadlineChange)}
          onClose={onClose}
          onRemove={canRemove('deadline', !!deadlineValue) ? () => handlers.onDeadlineChange(null) : undefined}
          placement="actionbar"
        />
      )
    case 'attention':
      return (
        <AttentionDropdownContent
          value={task.attention ?? null}
          onValueChange={commit(handlers.onAttentionChange)}
          onClose={onClose}
          // Wie Start, Dauer und Deadline (#216): Entfernen, sobald eine dasteht (Sven, 10.10.2026).
          onRemove={canRemove('attention', task.attention != null) ? () => commit(handlers.onAttentionChange)(null) : undefined}
          placement="actionbar"
        />
      )
    case 'tags':
      return (
        <TagDropdownContent
          value={task.tags ?? []}
          onValueChange={commit(handlers.onTagsChange)}
          onClose={onClose}
          // **Denselben Fuß wie an der Marke** (angeglichen 2026-09-19): Wer
          // Tags trägt, bekommt „Alle Tags entfernen"; wer keine trägt und
          // nichts angefasst hat, bekommt gar keinen Fuß. Der Griff sagt das,
          // nicht der Einbauort — deshalb steht er hier und nicht als Regel im
          // Kasten.
          onRemoveAll={!canRemove('tags', (task.tags ?? []).length > 0) ? undefined
            : () => { handlers.onTagsChange([]); onClose() }}
          placement="actionbar"
        />
      )
    case 'reward':
      return (
        <RewardContent
          value={task.rewardId ?? null}
          removable={canRemove('reward', task.rewardId != null)}
          onValueChange={handlers.onRewardChange}
          onClose={onClose}
        />
      )
    case 'recurrence':
      return (
        <RecurrenceDropdownContent
          value={task.recurrence ?? null}
          seedDate={task.startDate ?? null}
          onValueChange={commit(handlers.onRecurrenceChange)}
          // „Nie" im Auswahlfeld: Die Menü-Fassung hat keinen Papierkorb — ohne den
          // Eintrag gäbe es hier keinen Weg, eine Wiederholung wieder abzunehmen.
          //
          // **Ohne `commit`, also ohne Schließen** (Nutzer-Vorgabe, 2026-09-19): „Nie"
          // war das Einzige im Kasten, das die Tür hinter sich zuzog. Wer sich vertat,
          // musste das Menü neu öffnen — dabei sind Abnehmen und neu Wählen EIN Gedanke.
          // Geschlossen wird hier wie überall: durch einen Klick daneben.
          onClear={() => handlers.onRecurrenceChange(null)}
          onClose={onClose}
          placement="actionbar"
        />
      )
    // Verschieben hat sein eigenes Panel an der Zeile (`MoveShortcutBox`, #263).
    case 'move':
      return null
  }
}
